import { usePurchaseContext } from '~/purchase/context';
import { Trans } from '~/common/context';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Card, Flex, FormContainer, Modal, notify } from '@wyz/ui';
import { useForm, useWatch } from 'react-hook-form';
import { useManageRedistributionServicePricingPolicies } from '~/purchase/api';
import { duplicatePolicyFormSchema } from '~/purchase/schemas';
import { usePricingPolicyErrorTranslation } from '~/common/hooks';
import { IDuplicatePolicyForm } from '~/purchase/types';
import { ConflictedPoliciesTable } from '~/purchase/components';
import { omit } from 'lodash-es';
import { invariant } from '@wyz/utils';

type ServiceDuplicatePolicyModalProps = {
  onClose: () => void;
  policyGroupId: number;
  customerId: string;
};

function RedistributionServiceDuplicatePolicyModal({
  customerId,
  onClose,
  policyGroupId,
}: ServiceDuplicatePolicyModalProps) {
  const methods = useForm<IDuplicatePolicyForm>({
    resolver: zodResolver(duplicatePolicyFormSchema),
    defaultValues: {
      customerIds: [],
      action: 'SAVE_AS_DRAFT',
    },
  });
  const watchedCustomers = useWatch({
    control: methods.control,
    name: 'customerIds',
    defaultValue: [],
  });
  const { state } = usePurchaseContext();
  const { resolveErrorKey } = usePricingPolicyErrorTranslation();
  invariant(state.targetCustomer);

  const manageRedistributionServicePricingPolicies =
    useManageRedistributionServicePricingPolicies({
      getConflicted: {
        enabled: true,
        args: {
          id: policyGroupId,
          customerIds: watchedCustomers.map((c) => c.value),
        },
        staleTime: 0,
        gcTime: 0,
      },
      duplicate: {
        enabled: true,
      },
    });

  return (
    <Modal.Container open={true} onClose={onClose} size='xl' backdrop='static'>
      <Modal.Header>
        <Modal.Title>
          <Trans i18nKey='app.quote.pricing_policy.duplicate_modal.title' />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FormContainer
          methods={methods}
          submitHandler={(form) => {
            manageRedistributionServicePricingPolicies.duplicate.mutate(
              {
                id: policyGroupId.toString(),
                body: {
                  ...omit(form, 'customerIds'),
                  customers: form.customerIds.map((customer) => ({
                    id: customer.value,
                    supplierTypes: [],
                  })),
                },
              },
              {
                onSuccess: () => {
                  notify.success(
                    <Trans i18nKey='app.common.messages.success' />,
                    {
                      id: 'toast-success-redistribution-service-duplicate-policy',
                    },
                  );
                  onClose();
                },
                onError: (error) => {
                  notify.error(
                    <Trans i18nKey={resolveErrorKey(error.message)} />,
                    {
                      id: 'toast-error-redistribution-service-duplicate-policy',
                    },
                  );
                },
              },
            );
          }}
        >
          <div className='space-y-4'>
            <ConflictedPoliciesTable
              conflictedPolicies={
                manageRedistributionServicePricingPolicies.getConflicted.data
                  ?.data ?? []
              }
              customerId={customerId}
              isLoadingPolicies={
                manageRedistributionServicePricingPolicies.getConflicted
                  .isLoading
              }
              customer={state.targetCustomer}
            />

            <Card>
              <Card.Body>
                <Flex className='space-x-2' justifyContent='end'>
                  <Button
                    id='cancel'
                    variant='secondary'
                    type='button'
                    onClick={onClose}
                    disabled={
                      manageRedistributionServicePricingPolicies.duplicate
                        .isLoading ||
                      manageRedistributionServicePricingPolicies.getConflicted
                        .isLoading
                    }
                  >
                    <Trans i18nKey='app.common.button.cancel' />
                  </Button>
                  <Button
                    id='apply'
                    variant='primary'
                    type='submit'
                    isLoading={
                      manageRedistributionServicePricingPolicies.duplicate
                        .isLoading ||
                      manageRedistributionServicePricingPolicies.getConflicted
                        .isLoading
                    }
                  >
                    <Trans i18nKey='app.common.button.apply' />
                  </Button>
                </Flex>
              </Card.Body>
            </Card>
          </div>
        </FormContainer>
      </Modal.Body>
    </Modal.Container>
  );
}

export default RedistributionServiceDuplicatePolicyModal;
